/**
 * Skala kedalaman — berapa piksel satu meter batuan, dipakai bersama layar
 * Workstation dan layar korelasi.
 *
 * Kedua layar sebelumnya memuat seluruh sumur ke dalam tinggi panel apa pun.
 * Itu bukan skala, melainkan kebetulan: lembar yang sama tampak 1:400 di
 * laptop dan 1:180 di monitor besar, dan angka "1:200" di kaki layar tidak
 * pernah menjadi janji yang bisa ditagih. Modul ini membuat angka itu
 * menentukan tinggi, bukan sekadar melaporkannya — sehingga satu lapisan
 * setebal dua meter punya ukuran yang sama di mana pun ia dibaca.
 */

/** Piksel CSS per milimeter. CSS mendefinisikan satu inci sebagai 96 piksel. */
export const CSS_PX_PER_MM = 96 / 25.4;

/**
 * Rentang yang boleh dipilih sendiri: 1:20 untuk lapisan tipis, 1:5000 untuk
 * tinjauan. Mode pas boleh melampaui batas atas — sumur 1800 m di panel
 * setinggi 640 piksel memang 1:10600, dan angka itu harus dilaporkan apa
 * adanya, bukan dijepit sampai berbohong.
 */
export const DEPTH_SCALE_MIN = 20;
export const DEPTH_SCALE_MAX = 5000;

/** Skala baku yang selalu sejangkauan satu klik. */
export const DEPTH_SCALE_PRESETS = [50, 100, 200, 500, 1000];

/** Satu langkah regang atau mampat: seperlima lebih besar atau lebih kecil. */
export const DEPTH_SCALE_STEP = 1.25;

/**
 * Mode skala sebuah panel.
 *
 * `"fit"` mempertahankan perilaku lama — seluruh sumur dipaksa muat — dan
 * tetap menjadi bawaan, sebab layar yang tiba-tiba memperbesar diri sepuluh
 * kali saat dibuka bukan perbaikan. Angka berarti skala sungguhan.
 */
export type ScaleMode = number | "fit";

/** Skala yang dipilih pengguna, dijepit ke rentang yang bisa dipilih. */
export function clampDepthScale(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 0) return 200;
  return Math.min(DEPTH_SCALE_MAX, Math.max(DEPTH_SCALE_MIN, Math.round(scale)));
}

/** Skala apa pun yang masuk akal secara aritmetika, termasuk hasil mode pas. */
function usableScale(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 0) return 200;
  return Math.max(DEPTH_SCALE_MIN, Math.round(scale));
}

/** Piksel layar per satu meter kedalaman pada skala 1:`scale`. */
export function pxPerMetre(scale: number): number {
  return (1000 / usableScale(scale)) * CSS_PX_PER_MM;
}

/** Skala yang membuat `metres` tepat memenuhi `viewportPx` piksel. */
export function fitScale(metres: number, viewportPx: number): number {
  if (!(metres > 0) || !(viewportPx > 0)) return 200;
  return usableScale((metres * 1000 * CSS_PX_PER_MM) / viewportPx);
}

/**
 * Satu langkah regang (`direction` 1) atau mampat (`-1`).
 *
 * Meregang berarti satu meter mendapat lebih banyak piksel, dan itu berarti
 * angka skalanya mengecil — 1:100 lebih regang daripada 1:200. Pembulatan
 * dilakukan di sini supaya daftar skala tidak dipenuhi angka seperti 1:327.
 */
export function stepScale(scale: number, direction: 1 | -1): number {
  const next = direction === 1 ? scale / DEPTH_SCALE_STEP : scale * DEPTH_SCALE_STEP;
  return clampDepthScale(Math.round(next));
}

/**
 * Tinggi isi panel dan skala yang benar-benar berlaku.
 *
 * `heightPx` adalah tinggi sesungguhnya, juga ketika lebih pendek daripada
 * panelnya: memampatkan sumur sampai setengah tinggi panel lalu meregangkannya
 * kembali agar "penuh" akan membatalkan pemampatan yang barusan diminta.
 */
export function resolveScale(
  mode: ScaleMode,
  metres: number,
  viewportPx: number,
  gridPx?: number,
): { scale: number; pxPerMetre: number; heightPx: number; fitted: boolean } {
  const fitted = mode === "fit";
  const scale = fitted ? fitScale(metres, viewportPx) : clampDepthScale(mode);
  const perMetre = pxPerMetre(scale);
  // Dalam mode pas, tinggi panel yang menentukan — bukan hasil pembulatan
  // skalanya — supaya tidak muncul batang gulir setinggi dua piksel.
  const heightPx = fitted ? viewportPx : Math.max(metres * perMetre, 1);
  if (!fitted && gridPx && gridPx > 0 && metres > 0) {
    // Lembar setinggi bilangan bulat satuan kisi. Kisi mendatarnya digambar
    // sebagai pengulangan setiap `gridPx` piksel dari puncak lembar, jadi
    // tinggi yang bukan kelipatannya berakhir dengan satu petak buntung di
    // kaki lembar — dan petak buntung itu berpindah tempat pada setiap
    // kejadian roda, sehingga yang terlihat bergerak ketika seseorang
    // memperbesar bukan hanya kurvanya melainkan kisinya sendiri.
    //
    // Skalanya dihitung ulang dari tinggi yang sudah dibulatkan, bukan
    // dipertahankan apa adanya: angka "1:200" yang tercetak di kaki layar
    // harus angka yang benar-benar berlaku, dan sesudah pembulatan ia bukan
    // lagi angka yang diminta.
    const snapped = Math.max(Math.round(heightPx / gridPx), 1) * gridPx;
    return {
      scale: clampDepthScale(fitScale(metres, snapped)),
      pxPerMetre: snapped / metres,
      heightPx: snapped,
      fitted,
    };
  }
  return { scale, pxPerMetre: perMetre, heightPx, fitted };
}

/**
 * Skala sesudah lembar ditambah atau dikurangi **satu satuan kisi**.
 *
 * Tombol + dan − pada lembar berkisi tidak melangkah seperlima seperti
 * `stepScale`: langkah yang tidak berkelipatan satuan kisi memindahkan setiap
 * garis kisi sedikit pada setiap tekanan, dan yang dibaca orang di lembar log
 * adalah jarak antar garis. Satu tekanan karena itu berarti satu petak lebih
 * tinggi atau lebih pendek — persis satu garis lagi.
 */
export function stepScaleByGrid(
  metres: number,
  heightPx: number,
  gridPx: number,
  direction: 1 | -1,
): number {
  if (!(metres > 0) || !(gridPx > 0) || !(heightPx > 0)) return 200;
  const cells = Math.max(Math.round(heightPx / gridPx), 1);
  return clampDepthScale(fitScale(metres, Math.max(cells + direction, 1) * gridPx));
}

/**
 * Berapa piksel seret tegak yang menggandakan atau menyetengahkan skala.
 *
 * Angkanya dipilih supaya seluruh rentang yang bisa dipilih — 1:20 sampai
 * 1:5000, delapan penggandaan — terjangkau dalam satu seret sepanjang layar,
 * dan supaya seret sejauh satu ruas jari masih berupa perubahan yang bisa
 * diikuti mata alih-alih lompatan.
 */
export const DEPTH_DRAG_DOUBLE = 180;

/**
 * Skala sesudah mistar kedalaman diseret sejauh `dy` piksel.
 *
 * Menyeret ke bawah meregangkan lembar: gerakannya sama dengan menarik ujung
 * bawah sumur menjauh dari ujung atasnya. Bahwa angka skalanya lalu mengecil
 * adalah akibat, bukan arah — 1:100 lebih regang daripada 1:200.
 *
 * Berbeda dengan `stepScale`, di sini tidak ada pembulatan ke angka bulat
 * sampai penjepitan terakhir: sebuah seret adalah satu gerakan menerus, dan
 * membulatkan setiap kejadian pointer di tengah jalan membuat lembarnya
 * bergetar alih-alih mengikuti tangan.
 */
export function dragScale(startScale: number, dy: number): number {
  if (!Number.isFinite(startScale) || startScale <= 0) return 200;
  if (!Number.isFinite(dy)) return clampDepthScale(startScale);
  return clampDepthScale(startScale * 2 ** (-dy / DEPTH_DRAG_DOUBLE));
}

/**
 * Skala yang membuat sebuah interval kedalaman memenuhi panel — zoom kotak.
 *
 * Ini `fitScale` yang dijepit ke rentang yang boleh dipilih sendiri. Mode pas
 * boleh melampaui batas itu karena ia melaporkan keadaan; zoom kotak memilih
 * keadaan, dan pilihan yang tidak bisa dikembalikan lewat daftar skala biasa
 * adalah jebakan.
 */
export function intervalScale(metres: number, viewportPx: number): number {
  return clampDepthScale(fitScale(metres, viewportPx));
}

/**
 * Seberapa jauh satu kejadian roda dihitung sebagai seret.
 *
 * Satu takik roda tetikus biasa melaporkan 100 piksel; dikalikan angka ini ia
 * menjadi 60 piksel seret, yaitu sekitar satu langkah `stepScale` — sehingga
 * roda terasa sama dengan tombol + dan −, sementara papan sentuh yang
 * melaporkan puluhan kejadian kecil tetap bergerak halus alih-alih melompat
 * satu langkah penuh per kejadian.
 */
export const WHEEL_ZOOM_GAIN = 0.6;

/**
 * Jarak setara dalam piksel dari satu kejadian roda.
 *
 * `deltaY` dilaporkan dalam satuan yang berbeda-beda: piksel pada kebanyakan
 * peramban, baris pada Firefox dengan setelan tertentu, dan halaman penuh pada
 * beberapa tetikus. Tanpa penyetaraan ini, satu takik roda yang sama
 * memperbesar seperlima di satu peramban dan tiga puluh kali di peramban lain.
 */
export function wheelPixels(deltaY: number, deltaMode = 0): number {
  if (!Number.isFinite(deltaY)) return 0;
  const unit = deltaMode === 1 ? 16 : deltaMode === 2 ? 400 : 1;
  return deltaY * unit;
}

/**
 * Berapa tera yang pantas untuk mistar setinggi `heightPx`.
 *
 * Namanya menyebut kedalaman, bukan sekadar "sumbu": `tracklayout` sudah punya
 * `axisTicks` yang menjawab pertanyaan yang sama sekali lain — angka berapa
 * yang tercetak di sumbu mendatar sebuah kurva.
 *
 * Cacah tetap sebelas benar selama lembarnya sepanjang satu layar, dan salah
 * begitu ia lebih panjang: pada 1:99 sebuah jendela 260 m menjadi lembar
 * setinggi sebelas ribu piksel, dan sebelas tera di sepanjang itu berarti satu
 * angka tiap seribu piksel — menggulir satu layar penuh tanpa melihat satu
 * kedalaman pun. Sembilan puluh piksel adalah jarak yang sama dengan yang
 * dihasilkan sebelas tera pada lembar sepanjang satu layar, jadi lembar yang
 * memang sepanjang satu layar tidak berubah sama sekali.
 */
export function depthTickCount(heightPx: number): number {
  if (!Number.isFinite(heightPx) || heightPx <= 0) return 11;
  return Math.min(160, Math.max(11, Math.round(heightPx / 90)));
}
